"use client"
import { useInfiniteContent } from "@/app/hooks/useInfiniteContent"
import LoadingContent from "@/components/ui/LoadingContent"
import ShortHero from "@/components/ui/ShortHero"
import VerticalCard from "@/components/ui/VerticalCard"
import { useCallback, useEffect, useRef, useState } from "react"
import Hero from "../work/Hero"
import { usePathname } from "next/navigation"
import { Skeleton } from "../ui/skeleton"

export default function PostList({type= "insights", title= "Insights" , description= ""}) {

  const pathname = usePathname()

  let contentType

  if (type === "insights") {
    contentType = "posts"
  }

  if (type === "work") {
    contentType = "work"
  }


  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteContent(contentType, 4)
  const loadMoreRef = useRef(null)
  const [playAnimation, setPlayAnimation] = useState(false)

  // Create a flattened array of all posts with global indices
  const allPosts = data?.pages.flatMap(page => page?.posts || []) || []

  const handleIntersection = useCallback(
    entries => {
      const [entry] = entries
      if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage()
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage]
  )

  useEffect(() => {
    const currentRef = loadMoreRef.current
    if (!currentRef) return

    const observer = new IntersectionObserver(handleIntersection, {
      threshold: 1,
      rootMargin: "0px 0px 600px 0px" // Load earlier before reaching the bottom
    })

    observer.observe(currentRef)

    return () => {
      if (currentRef) observer.disconnect()
    }
  }, [handleIntersection])


  useEffect(() => {
    const flag = sessionStorage.getItem("playAnimation")
    if (flag && type === "work") {
      setPlayAnimation(true)
    }
  }, [])

  return (
    <div className="pb-56">
      {type === "work" ? (
        playAnimation ? (
          <div className="container mx-auto">
            <ShortHero title={title} description={description} />
          </div>
        ) : (
          <Hero key={pathname} />
        )
      ) : (
        <div className="container mx-auto">
          <ShortHero title={title} description={description} />
        </div>
      )}

      <div className="container mt-32 mx-auto">
        <section className="grid grid-cols-1 md:grid-cols-2 gap-12 mx-auto ">
          {allPosts.map((post, globalIndex) => (
            <VerticalCard content={post} key={post.id} withExcerpt={true} index={globalIndex} />
          ))}
        </section>
        {hasNextPage && (
          <div ref={loadMoreRef} className="relative text-center p-4 mt-8 h-screen" aria-hidden="true">
            {isFetchingNextPage && (
              <section className="grid grid-cols-1 md:grid-cols-2 gap-12 mx-auto">
                <article className="flex flex-col">
                  <div>
                    <Skeleton className="relative aspect-[16/9] w-full h-full mb-6 md:mb-8 animate-pulse rounded-xl" />
                  </div>
                  <div className="flex flex-col items-start group-hover:gap-x-2 gap-y-3 mt-4">
                    <Skeleton className="animate-pulse h-8 w-full rounded-full" />
                    <Skeleton className="animate-pulse h-8 w-full rounded-full mb-4" />
                    <Skeleton className="animate-pulse h-6 w-full rounded-full" />
                    <Skeleton className="animate-pulse h-6 w-1/2 rounded-full" />
                  </div>
                </article>
                <article className="flex flex-col">
                  <div>
                    <Skeleton className="relative aspect-[16/9] w-full h-full mb-6 md:mb-8 animate-pulse rounded-xl" />
                  </div>
                  <div className="flex flex-col items-start group-hover:gap-x-2 gap-y-3 mt-4">
                    <Skeleton className="animate-pulse h-8 w-full rounded-full" />
                    <Skeleton className="animate-pulse h-8 w-full rounded-full mb-4" />
                    <Skeleton className="animate-pulse h-6 w-full rounded-full" />
                    <Skeleton className="animate-pulse h-6 w-1/2 rounded-full" />
                  </div>
                </article>
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
